'use client';

import { useEffect, useRef } from 'react';
import TransCustom from '../../ui/transCustom/TransCustom';

type FooterVisualBandVariant = 'desktop' | 'mobile';

type Props = {
  variant: FooterVisualBandVariant;
};

const BACKGROUND_CLASS: Record<FooterVisualBandVariant, string> = {
  desktop: 'footer-visual-band footer-visual-band--desktop',
  mobile: 'footer-visual-band footer-visual-band--mobile',
};

const TEXT_CLASS: Record<FooterVisualBandVariant, string> = {
  desktop:
    'px-10 py-[clamp(47px,8.125vw,156px)] text-[clamp(36px,4.167vw,80px)] leading-[160%] tracking-[-1.6px]',
  mobile: 'px-10 py-[47px] text-[36px] leading-[120%] tracking-[-0.72px]',
};

/**
 * ### FooterVisualBand
 *
 * Renders the closing brand statement over the former home-story video.
 * The decorative video is assigned during browser idle time and falls back to
 * its poster when motion is reduced or autoplay is unavailable.
 *
 * @param props Desktop or mobile band layout
 * @returns The rendered footer visual band
 */
export default function FooterVisualBand({ variant }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const idleWindow = window as Window & {
      requestIdleCallback?: (
        callback: () => void,
        options?: { timeout: number },
      ) => number;
      cancelIdleCallback?: (handle: number) => void;
    };

    const loadVideo = () => {
      video.src = '/images/apps/home/sec_01_video_01.mp4';
      video.load();

      // Autoplay can still be refused by a browser policy; the poster remains the fallback.
      void video.play().catch(() => undefined);
    };

    if (idleWindow.requestIdleCallback) {
      const handle = idleWindow.requestIdleCallback(loadVideo, { timeout: 2000 });

      return () => idleWindow.cancelIdleCallback?.(handle);
    }

    const handle = window.setTimeout(loadVideo, 1200);

    return () => window.clearTimeout(handle);
  }, []);

  return (
    <div className={BACKGROUND_CLASS[variant]}>
      <video
        ref={videoRef}
        className="footer-visual-band__video"
        aria-hidden="true"
        loop
        muted
        playsInline
        preload="none"
        poster="/images/apps/home/sec_01_video_01_poster.webp"
      />
      <span
        className={`relative z-[2] block text-center font-bold text-apps-coreGray07 ${TEXT_CLASS[variant]}`}
      >
        <TransCustom i18nKey="소리에 의미를 더하다" />
      </span>
    </div>
  );
}
